import { useState } from "react";
import { styles } from "../styles";
import HighlightText from "../components/HighlightText";
import { serviceTagOptions } from "../utils/constants";
import BackButton from "../components/BackButton";
import TypedConfirmModal from "../components/TypedConfirmModal";

export default function RecentActivityPage({
  customers,
  actions,
  user,
  role,
  orgId,
  orgType,
  setSelectedCustomer,
  setSelectedLocation,
  setSelectedEquipment,
  setSelectedContactLocation,
  onBack,
}) {
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected({});
  };

  const canAcknowledge =
    role === "admin" ||
    role === "manager" ||
    role === "executive" ||
    role === "facilities_manager";
  const canDelete = canAcknowledge;
  const isFacility = orgType === "facility";

  const allRecords = customers
    .flatMap((customer) =>
      (customer.locations || []).flatMap((location) =>
        (location.equipment || []).flatMap((equipment) =>
          (equipment.serviceRecords || [])
            .filter((record) => !record.deleted)
            .filter(
              (record) => !(record.hiddenByOrgs || []).includes(orgId)
            )
            .map((record) => ({
              ...record,
              customerId: customer.id,
              customerName: customer.name,
              locationId: location.id,
              locationName: location.name,
              equipmentId: equipment.id,
              equipmentName: equipment.type,
            }))
        )
      )
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
    );

  const words = search.toLowerCase().split(" ").filter((w) => w);

  const filtered = allRecords.filter((record) => {
    const haystack = `${record.notes || ""} ${record.customerName} ${
      record.locationName
    } ${record.equipmentName}`.toLowerCase();
    const matchesSearch = words.every((word) => haystack.includes(word));

    const matchesTag = !tagFilter || record.tag === tagFilter;

    const recordTime = new Date(record.createdAt || record.date).getTime();
    let matchesFrom = true;
    let matchesTo = true;
    if (fromDate) {
      matchesFrom = recordTime >= new Date(`${fromDate}T00:00:00`).getTime();
    }
    if (toDate) {
      const next = new Date(`${toDate}T00:00:00`);
      next.setDate(next.getDate() + 1);
      matchesTo = recordTime < next.getTime();
    }

    return matchesSearch && matchesTag && matchesFrom && matchesTo;
  });

  const unackInView = filtered.filter((r) => !r.acknowledged);

  const goToEquipment = (record) => {
    const customer = customers.find((c) => c.id === record.customerId);
    const location = customer?.locations.find(
      (l) => l.id === record.locationId
    );
    const equipment = location?.equipment.find(
      (e) => e.id === record.equipmentId
    );
    if (!equipment) return;
    setSelectedCustomer(customer);
    setSelectedLocation(location);
    setSelectedEquipment(equipment);
    if (setSelectedContactLocation) setSelectedContactLocation(null);
    onBack();
  };

  const clearFilters = () => {
    setSearch("");
    setTagFilter("");
    setFromDate("");
    setToDate("");
  };

  const acknowledgeOne = (record) =>
    actions?.updateServiceRecord?.(record.id, {
      acknowledged: true,
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: user?.email || "",
    });

  // Contractor (owner) deletes mean a real soft-delete on the source
  // record. Facility-side "delete" just adds my orgId to hiddenByOrgs
  // on the same shared doc, so it disappears from MY org's view while
  // the contractor's audit trail stays intact.
  const deleteOne = (record) => {
    if (isFacility) {
      const next = [
        ...new Set([...(record.hiddenByOrgs || []), orgId]),
      ];
      return actions?.updateServiceRecord?.(record.id, {
        hiddenByOrgs: next,
      });
    }
    return actions?.softDeleteServiceRecord?.(record.id, user?.email);
  };

  const acknowledgeSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    const targets = filtered.filter(
      (r) => ids.includes(r.id) && !r.acknowledged
    );
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Acknowledge ${targets.length} selected record(s)? Clears the NEW badge for everyone in your org.`
      )
    )
      return;
    setBusy(true);
    try {
      for (const r of targets) {
        await acknowledgeOne(r);
      }
    } finally {
      setBusy(false);
    }
  };

  // Bulk delete queues the candidate ids and opens a typed-confirm modal.
  // The old window.confirm was getting tripped by accidental clicks
  // (Ack and Delete sit next to each other in the toolbar) — typing
  // DELETE makes that impossible. See TypedConfirmModal for why.
  const [pendingBulkDelete, setPendingBulkDelete] = useState(null);

  const deleteSelected = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) return;
    setPendingBulkDelete(ids);
  };

  const confirmBulkDelete = async () => {
    const ids = pendingBulkDelete || [];
    if (ids.length === 0) {
      setPendingBulkDelete(null);
      return;
    }
    setBusy(true);
    try {
      for (const id of ids) {
        const r = filtered.find((x) => x.id === id);
        if (r) await deleteOne(r);
      }
      exitSelectMode();
    } finally {
      setBusy(false);
      setPendingBulkDelete(null);
    }
  };

  const acknowledgeAll = async () => {
    if (unackInView.length === 0) return;
    if (
      !window.confirm(
        `Acknowledge ${unackInView.length} service record(s) shown? Clears the NEW badge for everyone in your org.`
      )
    )
      return;
    setBusy(true);
    try {
      for (const r of unackInView) {
        await acknowledgeOne(r);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "12px",
          flexWrap: "wrap",
          margin: "0 0 8px",
        }}
      >
        <h2 style={{ margin: 0 }}>Recent Activity</h2>
        <span style={{ fontSize: "13px", color: "#666" }}>
          {filtered.length} of {allRecords.length} records
          {unackInView.length > 0 && (
            <>
              {" · "}
              <strong>{unackInView.length} New</strong>
            </>
          )}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          margin: "0 0 8px",
        }}
      >
        <button
          style={styles.button}
          onClick={() => setShowFilters((s) => !s)}
        >
          {showFilters ? "▼ Filter" : "▶ Filter"}
        </button>
        {canAcknowledge && unackInView.length > 0 && !selectMode && (
          <button
            style={styles.button}
            onClick={acknowledgeAll}
            disabled={busy}
          >
            {busy ? "Acknowledging..." : `Ack All (${unackInView.length})`}
          </button>
        )}
        {canDelete && !selectMode && filtered.length > 0 && (
          <button
            style={styles.button}
            onClick={() => setSelectMode(true)}
            title="Select records to delete"
          >
            ✓ Select
          </button>
        )}
        {selectMode && (
          <>
            <label style={{ cursor: "pointer", fontSize: "13px", marginLeft: "auto" }}>
              <input
                type="checkbox"
                checked={
                  filtered.length > 0 &&
                  filtered.every((r) => selected[r.id])
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    const next = {};
                    filtered.forEach((r) => (next[r.id] = true));
                    setSelected(next);
                  } else {
                    setSelected({});
                  }
                }}
              />{" "}
              Select All
            </label>
            {(() => {
              const ids = Object.keys(selected).filter((k) => selected[k]);
              const unackSelected = filtered.filter(
                (r) => ids.includes(r.id) && !r.acknowledged
              ).length;
              return (
                <>
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    {ids.length}
                  </span>
                  {canAcknowledge && (
                    <button
                      style={{ ...styles.button, margin: 0 }}
                      disabled={busy || unackSelected === 0}
                      onClick={acknowledgeSelected}
                    >
                      {busy
                        ? "..."
                        : `Ack${unackSelected ? ` (${unackSelected})` : ""}`}
                    </button>
                  )}
                  <button
                    style={{
                      ...styles.button,
                      margin: 0,
                      background: ids.length ? "#fee" : "#f3f4f6",
                      color: ids.length ? "#a00" : "#999",
                    }}
                    disabled={busy || ids.length === 0}
                    onClick={deleteSelected}
                  >
                    {busy
                      ? "..."
                      : `${isFacility ? "Remove" : "Delete"}${
                          ids.length ? ` (${ids.length})` : ""
                        }`}
                  </button>
                  <button
                    style={{ ...styles.button, margin: 0 }}
                    disabled={busy}
                    onClick={exitSelectMode}
                  >
                    Done
                  </button>
                </>
              );
            })()}
          </>
        )}
      </div>

      {showFilters && (
        <div style={styles.card}>
          <input
            style={styles.input}
            placeholder="Search notes, customer, location, equipment..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <br />
          <label>From: </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          {"  "}
          <label>To: </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <br />
          <label>Service Type: </label>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
          >
            <option value="">All</option>
            {serviceTagOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <br />
          <button style={styles.button} onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p style={{ color: "#666" }}>
          No service records match the current filters.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filtered.map((record) => (
            <li
              key={record.id}
              style={{
                ...styles.card,
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              {selectMode && (
                <input
                  type="checkbox"
                  checked={!!selected[record.id]}
                  onChange={(e) =>
                    setSelected((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[record.id] = true;
                      else delete next[record.id];
                      return next;
                    })
                  }
                  style={{ marginTop: "3px" }}
                />
              )}
              <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  <HighlightText
                    text={record.equipmentName}
                    search={search}
                  />
                </strong>
                <span style={{ color: "#666" }}>
                  at{" "}
                  <HighlightText
                    text={record.locationName}
                    search={search}
                  />{" "}
                  /{" "}
                  <HighlightText
                    text={record.customerName}
                    search={search}
                  />
                </span>
                {!record.acknowledged && (
                  <span
                    style={{
                      background: "#fff3cd",
                      color: "#9a6b00",
                      fontSize: "11px",
                      padding: "1px 6px",
                      borderRadius: "8px",
                      fontWeight: 700,
                    }}
                  >
                    NEW
                  </span>
                )}
              </div>
              <div style={{ marginTop: "4px" }}>
                <span style={{ fontSize: "12px", color: "#444" }}>
                  {record.tag || "Service Record"}
                </span>
              </div>
              <div style={{ marginTop: "4px" }}>
                <HighlightText text={record.notes || ""} search={search} />
              </div>
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                {record.date} • by{" "}
                {record.createdByName || record.createdBy || "unknown"}
                {record.createdByOrgName ? ` · ${record.createdByOrgName}` : ""}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  marginTop: "8px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  style={{ ...styles.button, margin: 0 }}
                  onClick={() => goToEquipment(record)}
                >
                  Go to Equipment
                </button>
                {canAcknowledge && !record.acknowledged && (
                  <button
                    style={{ ...styles.button, margin: 0 }}
                    onClick={() => acknowledgeOne(record)}
                  >
                    Acknowledge
                  </button>
                )}
              </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <TypedConfirmModal
        open={!!pendingBulkDelete}
        title={
          isFacility
            ? "Remove records from your view?"
            : "Delete service records?"
        }
        message={(() => {
          const count = (pendingBulkDelete || []).length;
          if (count === 0) return "";
          const verb = isFacility ? "Remove" : "Delete";
          const tail = isFacility
            ? "Removes from your org's view only — the source stays."
            : "Moves them to Deleted Items. Admin can restore from there.";
          return `${verb} ${count} record(s) shown?\n\n${tail}`;
        })()}
        confirmWord="DELETE"
        confirmLabel={
          isFacility
            ? `Remove ${(pendingBulkDelete || []).length} record(s)`
            : `Delete ${(pendingBulkDelete || []).length} record(s)`
        }
        busy={busy}
        onConfirm={confirmBulkDelete}
        onCancel={() => !busy && setPendingBulkDelete(null)}
      />
    </>
  );
}
